import React from 'react';
import { CertStateReview } from '../../../utils/api/certification';
import './_ReviewRequiredBanner.scss';

type Props = {
  review: CertStateReview | null | undefined;
  /** The device is offline (`useOffline`). */
  isOffline?: boolean;
  /** The signature that raises the review is saved on the device, waiting to upload. */
  signedOnDevice?: boolean;
  /** The viewer could sign here: the project is active and they hold Sign Off. */
  canSignHere?: boolean;
};

/**
 * SW2-2248 — the review-status notice, colocated with (and styled after) the existing
 * project-inactive banner in `CertificationUI.tsx`.
 *
 * Renders nothing for `review == null` — either the form doesn't require review, or the current
 * viewer isn't in the notice's internal audience (decision 7 / AC10). Either way the server has
 * already decided that for us; there is no separate frontend permission check to reapply here, see
 * `InspectionReviewService.isInternalAudience` / `buildReviewBlock`.
 *
 * Also renders nothing for `not_raised` — that's the ordinary pre-signing state, not something
 * to announce. Except offline (decision 11 / AC 3): signed now, the review is only raised when
 * the device syncs, so the inspector is told before signing. Once signed on the device, the note
 * under the line takes over (`queuedSignatureHint`). The reviewer is only resolved when the review
 * is raised, so `reviewerName` is null here today; the copy names them if the server ever sends it.
 */
const ReviewRequiredBanner: React.FC<Props> = ({
  review,
  isOffline,
  signedOnDevice,
  canSignHere = true,
}) => {
  // Not for someone who can't sign here: "You can sign now" would contradict the page.
  if (review?.status === 'not_raised' && isOffline && !signedOnDevice && canSignHere) {
    return (
      <div className='review-required-banner review-required-banner--offline'>
        This inspection needs review, and you&apos;re offline. You can sign now. It will be sent
        {review.reviewerName ? ` to ${review.reviewerName}` : ''} for review when this device
        syncs, and the inspection complete email is held until it&apos;s approved.
      </div>
    );
  }

  if (review == null || review.status === 'not_raised') {
    return null;
  }

  if (review.status === 'pending') {
    return (
      <div className='review-required-banner review-required-banner--pending'>
        This inspection is pending review
        {review.reviewerName ? ` by ${review.reviewerName}` : ''}. The inspection complete
        notification is on hold until it is approved.
      </div>
    );
  }

  if (review.status === 'rejected') {
    return (
      <div className='review-required-banner review-required-banner--rejected'>
        This inspection was sent back for revision
        {review.reviewerName ? ` by ${review.reviewerName}` : ''}.
        {review.rejectionReason ? ` Reason: ${review.rejectionReason}` : ''} Re-sign the
        inspection to submit it for review again.
      </div>
    );
  }

  if (review.status === 'approved') {
    return (
      <div className='review-required-banner review-required-banner--approved'>
        Reviewed and approved{review.reviewerName ? ` by ${review.reviewerName}` : ''}.
      </div>
    );
  }

  // Decision C: only seen once the flag is back on. Nobody reviewed it, so no reviewer is named.
  if (review.status === 'released') {
    return (
      <div className='review-required-banner review-required-banner--cancelled'>
        Sent without review: Inspection Review was turned off while this inspection was waiting
        for review, and its inspection complete report was released.
      </div>
    );
  }

  if (review.status === 'cancelled') {
    return (
      <div className='review-required-banner review-required-banner--cancelled'>
        The previous review request for this inspection was cancelled.
      </div>
    );
  }

  return null;
};

export default ReviewRequiredBanner;
